import Image from 'next/image';
import type { PickemDto, TeamMatchDetailDto } from '@/api/backoffice-schemas';
import { MatchStatusType } from '@/constants/match.const';
import { cn } from '@/utils/cn';

interface PickemTeamButtonSelectProps {
  item: PickemDto;
  team: TeamMatchDetailDto;
  isSelected: boolean;
  onClick: () => void;
  className?: string;
  side: 'left' | 'right';
}

export const PickemTeamButtonSelect = ({
  team,
  item,
  isSelected,
  onClick,
  className,
  side,
}: PickemTeamButtonSelectProps) => {
  const { isBetPlaced, isExpired, winnerChoiceId } = item;

  const isSelectedViaAPI = winnerChoiceId === team?.teamId;

  const isSelectedInternal = isSelectedViaAPI || isSelected;

  const isEndOfMatch = item.match?.status === MatchStatusType.FINISHED;

  const isWinningTeam =
    item.match?.winner?.externalId === team?.team?.externalId;
  const isLosingTeam =
    item.match?.winner?.externalId !== team?.team?.externalId;

  return (
    <button
      aria-disabled={isBetPlaced || isExpired}
      aria-label={`Select ${team.team?.name || 'team'} to win`}
      aria-pressed={isSelectedInternal}
      className={cn(
        className,
        'relative flex h-[52px] w-full items-center justify-center gap-3 rounded-lg border p-3 transition-colors duration-200',
        // Base border and hover states
        'border-blue-gray-o15 hover:bg-light-gray',
        // Selected state
        isSelectedInternal && 'border-light-blue bg-custom-gradient',
        (isBetPlaced || isExpired) && 'cursor-not-allowed',
        isWinningTeam && isEndOfMatch && 'border-green bg-green-o20',
        isLosingTeam && isEndOfMatch && 'border-red bg-red-o20'
      )}
      disabled={isBetPlaced || isExpired}
      onClick={onClick}
      type="button"
    >
      {side === 'left' && isEndOfMatch && (
        <div className="-translate-y-1/2 absolute top-1/2 left-3">
          {isWinningTeam && (
            <span className="font-semibold text-green text-xl uppercase">
              W
            </span>
          )}
          {isLosingTeam && (
            <span className="font-semibold text-red text-xl uppercase">L</span>
          )}
        </div>
      )}
      {team.team?.imageUrl && (
        <Image
          alt={team.team.name || ''}
          height={28}
          src={team.team?.imageUrl}
          width={28}
        />
      )}
      <span>{team.team?.name}</span>

      {side === 'right' && isEndOfMatch && (
        <div className="-translate-y-1/2 absolute top-1/2 right-3">
          {isWinningTeam && (
            <span className="font-semibold text-green text-xl uppercase">
              W
            </span>
          )}
          {isLosingTeam && (
            <span className="font-semibold text-red text-xl uppercase">L</span>
          )}
        </div>
      )}
    </button>
  );
};
